import { useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, FileText, X } from "lucide-react";
import type { CellValue, ColumnSchema, FileValue } from "../lib/types";
import { dateOnly, dateTime, money, number } from "../lib/format";

/** Colour a status word by what it means operationally, not by its spelling. */
const TONE: Record<string, string> = {
  completed: "bg-emerald-950 text-emerald-300 border-emerald-900",
  confirmed: "bg-sky-950 text-sky-300 border-sky-900",
  paid: "bg-emerald-950 text-emerald-300 border-emerald-900",
  converted: "bg-emerald-950 text-emerald-300 border-emerald-900",
  accepted: "bg-emerald-950 text-emerald-300 border-emerald-900",
  open: "bg-sky-950 text-sky-300 border-sky-900",
  new: "bg-sky-950 text-sky-300 border-sky-900",
  pending: "bg-amber-950 text-amber-300 border-amber-900",
  awaiting: "bg-amber-950 text-amber-300 border-amber-900",
  pending_payment: "bg-amber-950 text-amber-300 border-amber-900",
  pending_cancellation: "bg-orange-950 text-orange-300 border-orange-900",
  offered: "bg-amber-950 text-amber-300 border-amber-900",
  contacted: "bg-amber-950 text-amber-300 border-amber-900",
  cancelled: "bg-red-950 text-red-300 border-red-900",
  failed: "bg-red-950 text-red-300 border-red-900",
  rejected: "bg-red-950 text-red-300 border-red-900",
  declined: "bg-red-950 text-red-300 border-red-900",
  refunded: "bg-purple-950 text-purple-300 border-purple-900",
};

export function Badge({ value }: { value: string }) {
  const tone = TONE[value] || "bg-ink-800 text-ink-300 border-ink-700";
  return (
    <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[11px] font-medium ${tone}`}>
      {value}
    </span>
  );
}

function isFileValue(value: unknown): value is FileValue {
  return typeof value === "object" && value !== null && !Array.isArray(value) && "url" in value;
}

function isFileList(value: unknown): value is FileValue[] {
  return Array.isArray(value) && value.length > 0 && value.every(isFileValue);
}

/**
 * An uploaded file, previewable without leaving the record. Images and PDFs
 * open in a modal — checking a customer's inspection report is the common case
 * and it is not worth a tab each time. Anything else, and the "new tab" link,
 * hand off to the browser.
 */
function FileCell({ file }: { file: FileValue }) {
  const [open, setOpen] = useState(false);
  const type = file.content_type || "";
  const previewable = type.startsWith("image/") || type === "application/pdf";

  return (
    <>
      <span className="inline-flex items-center gap-2">
        {previewable ? (
          <button onClick={() => setOpen(true)} className="inline-flex items-center gap-1 text-accent hover:underline">
            <FileText className="h-3.5 w-3.5" /> {file.name}
          </button>
        ) : (
          <span className="inline-flex items-center gap-1 text-ink-300">
            <FileText className="h-3.5 w-3.5" /> {file.name}
          </span>
        )}
        <a
          href={file.url}
          target="_blank"
          rel="noreferrer"
          className="text-ink-500 hover:text-ink-300"
          title="Öppna i ny flik"
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </span>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setOpen(false)}
        >
          <div className="panel flex h-full w-full max-w-4xl flex-col p-3" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center gap-3">
              <span className="truncate text-sm text-ink-200">{file.name}</span>
              <a href={file.url} target="_blank" rel="noreferrer"
                 className="ml-auto inline-flex items-center gap-1 text-xs text-ink-500 hover:text-ink-300">
                Öppna i ny flik <ExternalLink className="h-3 w-3" />
              </a>
              <button onClick={() => setOpen(false)} className="text-ink-500 hover:text-ink-200">
                <X className="h-4 w-4" />
              </button>
            </div>
            {type.startsWith("image/") ? (
              <img src={file.url} alt={file.name} className="min-h-0 flex-1 object-contain" />
            ) : (
              <iframe src={file.url} title={file.name} className="min-h-0 flex-1 rounded bg-white" />
            )}
          </div>
        </div>
      )}
    </>
  );
}

function isLinkValue(value: CellValue): value is { id: number; label: string } {
  return typeof value === "object" && value !== null && !Array.isArray(value) && "id" in value;
}

export function Cell({ column, value }: { column: ColumnSchema; value: CellValue }) {
  if (value === null || value === undefined || value === "") return <span className="text-ink-600">—</span>;

  if (isFileValue(value)) return <FileCell file={value} />;

  // A has_many_attached column — a booking's photos. An empty one falls
  // through to the json case, which already renders the dash.
  if (isFileList(value)) {
    return (
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {value.map((file, i) => <FileCell key={i} file={file} />)}
      </div>
    );
  }

  if (isLinkValue(value)) {
    return column.link ? (
      <Link
        to={`/r/${column.link}/${value.id}`}
        className="text-accent hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {value.label}
      </Link>
    ) : (
      <>{value.label}</>
    );
  }

  switch (column.type) {
    case "money":
      return <span className="num">{money(Number(value))}</span>;
    case "int":
      // An id links to its own detail page when the column says so.
      if (column.link && Number(value) > 0) {
        return (
          <Link to={`/r/${column.link}/${value}`} className="text-accent hover:underline"
                onClick={(e) => e.stopPropagation()}>
            #{String(value)}
          </Link>
        );
      }
      return <span className="num">{number(Number(value))}</span>;
    case "datetime":
      return <span className="num text-ink-400">{dateTime(String(value))}</span>;
    case "date":
      return <span className="num text-ink-400">{dateOnly(String(value))}</span>;
    case "bool":
      return value ? (
        <span className="text-emerald-400">Ja</span>
      ) : (
        <span className="text-ink-500">Nej</span>
      );
    case "badge":
      return <Badge value={String(value)} />;
    case "rating":
      return <span className="num text-amber-400">{Number(value).toFixed(1)} ★</span>;
    case "email":
      return <a href={`mailto:${value}`} className="text-accent hover:underline">{String(value)}</a>;
    case "phone":
      return <a href={`tel:${value}`} className="text-accent hover:underline">{String(value)}</a>;
    case "text":
      return <span className="text-ink-400" title={String(value)}>{truncate(String(value), 80)}</span>;
    case "hours": {
      const hours = value as unknown as Record<string, { open: string; close: string }>;
      const days = Object.entries(hours);
      if (days.length === 0) return <span className="text-ink-600">—</span>;
      return (
        <div className="text-xs text-ink-300">
          {days.map(([day, h]) => <div key={day}>{day}: {h.open}–{h.close}</div>)}
        </div>
      );
    }
    case "json":
      if (Array.isArray(value)) {
        if (value.length === 0) return <span className="text-ink-600">—</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {value.map((item, i) => (
              <span key={i} className="rounded bg-ink-800 px-1.5 py-0.5 text-[11px] text-ink-300">
                {typeof item === "object" ? JSON.stringify(item) : String(item)}
              </span>
            ))}
          </div>
        );
      }
      return <code className="text-[11px] text-ink-400">{JSON.stringify(value)}</code>;
    default:
      return <>{String(value)}</>;
  }
}

function truncate(text: string, max: number) {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}
